import { Card } from '@/components/ui/card';
import { formatCount } from '@/lib/format';

export interface BreakdownRow {
  key: string;
  label: string;
  count: number;
}

/**
 * One of the two all-time breakdown tiles — talents by category, organizations by kind.
 *
 * The API sends every category and every kind, zeros included and in catalog order, so
 * the tile renders the list as given: a zero row is a fact worth showing a club ("no
 * legal staff yet"), not a gap. The bar is relative to the largest row, never wider than
 * the card and never invisible for a row that has any count at all.
 */
export function BreakdownCard({
  heading,
  hint,
  rows,
}: {
  heading: string;
  hint: string;
  rows: readonly BreakdownRow[];
}) {
  const max = rows.reduce((m, row) => Math.max(m, row.count), 0) || 1;

  return (
    <Card className="p-5">
      <h3 className="text-[15px] font-bold text-navy">{heading}</h3>
      <p className="mb-4 text-[12.5px] text-conc-500">{hint}</p>
      <ul className="space-y-3">
        {rows.map((row) => (
          <li key={row.key} className="space-y-1">
            <div className="flex justify-between text-[12.5px]">
              <span className="font-semibold text-navy">{row.label}</span>
              <span className="num font-bold text-conc-600">{formatCount(row.count)}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-conc-100">
              <div
                className="h-full rounded-full bg-pitch"
                style={{
                  width:
                    row.count === 0 ? '0%' : `${Math.max(4, Math.round((row.count / max) * 100))}%`,
                }}
              />
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
